import { useState, type FormEvent } from 'react';
import { Copy, KeyRound, Link2, Plus, QrCode, Trash2, Unplug, UserPlus, Users } from 'lucide-react';
import { NavLink } from 'react-router';
import { createMemberResetLink, useIsTeamAdmin } from '../../api/team.ts';
import { ResetLinkPanel } from '../reset/ResetLinkPanel.tsx';
import type { CloudSite, MembershipRole } from '@ste/shared';
import { useAuth } from '../../api/auth.ts';
import { can, useConnection } from '../../api/connection.ts';
import { useMembers, useSiteCommands, useSites } from '../../api/sites.ts';
import { Banner, Button, Card, EmptyState, PageHeader, Spinner, Toggle, cx } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { CommandError } from '../charging/CommandError.tsx';

const inputClass = 'mt-1 block min-h-11 w-full rounded-lg border border-border bg-surface-2 px-3 text-text';

/** STE Connect: sites of the user, pairing and invitations */
export function SitesPage() {
  const sites = useSites();
  const user = useAuth((s) => s.user);
  const selectSite = useConnection((s) => s.selectSite);
  const commands = useSiteCommands();
  const [title, setTitle] = useState('');
  const teamAdmin = useIsTeamAdmin();

  const create = (event: FormEvent) => {
    event.preventDefault();
    commands.create.mutate(title, { onSuccess: (site) => (setTitle(''), selectSite(site.id)) });
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t('sites.title')}>
        {teamAdmin && (
          <NavLink
            to="/team"
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border bg-surface-2 px-4 font-medium text-text hover:border-muted"
          >
            <Users size={18} /> {t('team.manage')}
          </NavLink>
        )}
      </PageHeader>
      {user?.globalRole && (
        <Card title={t('sites.create')}>
          <form onSubmit={create} className="flex flex-wrap gap-2">
            <input
              aria-label={t('sites.siteTitle')}
              placeholder={t('sites.siteTitle')}
              className={cx(inputClass, 'mt-0 min-w-56 flex-1')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Button type="submit" disabled={!title.trim() || commands.create.isPending}>
              <Plus size={18} /> {t('sites.create')}
            </Button>
          </form>
          {commands.create.isError && <CommandError error={commands.create.error} />}
        </Card>
      )}
      {sites.isLoading && <Spinner label={t('common.loading')} />}
      {sites.data?.length === 0 && (
        <Card>
          <EmptyState>{t('sites.none')}</EmptyState>
        </Card>
      )}
      {sites.data?.map((site) => (
        <SiteCard key={site.id} site={site} />
      ))}
    </div>
  );
}

function SiteCard({ site }: { site: CloudSite }) {
  const commands = useSiteCommands();
  const selected = useConnection((s) => s.siteId === site.id);
  const selectSite = useConnection((s) => s.selectSite);
  const canConfigure = can(site.role, 'site:configure');
  const canMembers = can(site.role, 'site:members');
  const members = useMembers(site.id, canMembers);
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MembershipRole>(site.role === 'owner' ? 'member' : 'owner');
  const [link, setLink] = useState<string>();
  const [emailedTo, setEmailedTo] = useState<string>();
  const [resetFor, setResetFor] = useState<string>();
  // only STE staff create password reset links for customers (ADR-0015)
  const user = useAuth((s) => s.user);
  const staff = !!user?.globalRole;

  const roles: MembershipRole[] =
    site.role === 'owner' ? ['member', 'owner'] : ['owner', 'member', 'installer'];

  return (
    <Card
      title={site.title}
      action={
        <span
          className={cx('flex items-center gap-2 text-sm', site.online ? 'text-accent-text' : 'text-muted')}
        >
          <span className={cx('h-2.5 w-2.5 rounded-full', site.online ? 'bg-accent' : 'bg-warm')} />
          {site.deviceId ? (site.online ? t('sites.online') : t('sites.offline')) : t('sites.notPaired')}
        </span>
      }
    >
      <div className="space-y-5">
        {!selected && (
          <Button variant="secondary" onClick={() => selectSite(site.id)}>
            {t('sites.open')}
          </Button>
        )}

        {/* STE staff subscribe to the push notifications of any site (ADR-0025) */}
        {staff && site.subscribed !== undefined && (
          <div className="space-y-2">
            <Toggle
              label={t('sites.subscribe')}
              hint={t('sites.subscribeHint')}
              checked={site.subscribed}
              disabled={commands.subscribe.isPending}
              onChange={(subscribed) => commands.subscribe.mutate({ siteId: site.id, subscribed })}
            />
            {commands.subscribe.isError && <CommandError error={commands.subscribe.error} />}
          </div>
        )}

        {canConfigure && !site.deviceId && (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              commands.pair.mutate({ siteId: site.id, code }, { onSuccess: () => setCode('') });
            }}
          >
            <label className="block">
              <span className="flex items-center gap-2 text-sm text-muted">
                <QrCode size={16} /> {t('sites.pairingCode')}
              </span>
              <input
                className={inputClass}
                placeholder="STE1:ste-…:…"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            <p className="text-sm text-muted">{t('sites.pairingHint')}</p>
            <Button type="submit" disabled={code.trim().length < 10 || commands.pair.isPending}>
              <Link2 size={18} /> {t('sites.pair')}
            </Button>
            {commands.pair.isError && <CommandError error={commands.pair.error} />}
          </form>
        )}

        {canConfigure && site.deviceId && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted">
            <span>
              {t('sites.device')}: {site.serial ?? site.deviceId}
              {site.lastSeen && ` · ${new Date(site.lastSeen).toLocaleString('de-DE')}`}
            </span>
            <Button
              variant="ghost"
              onClick={() => window.confirm(t('sites.unpairConfirm')) && commands.unpair.mutate(site.id)}
            >
              <Unplug size={16} /> {t('sites.unpair')}
            </Button>
          </div>
        )}

        {canMembers && (
          <div className="space-y-3 border-t border-border pt-4">
            <h3 className="text-sm font-medium text-muted uppercase">{t('sites.members')}</h3>
            <ul className="divide-y divide-border">
              {members.data?.map((m) => (
                <li key={m.userId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{m.name}</div>
                    <div className="truncate text-sm text-muted">
                      {m.email} · {t(`sites.role.${m.role}`)}
                    </div>
                  </div>
                  <div className="flex">
                    {staff && m.userId !== user?.id && (
                      <Button
                        variant="ghost"
                        aria-label={`${t('reset.create')}: ${m.email}`}
                        title={t('reset.create')}
                        onClick={() => setResetFor(resetFor === m.userId ? undefined : m.userId)}
                      >
                        <KeyRound size={16} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      aria-label={`${t('installer.remove')}: ${m.email}`}
                      onClick={() => commands.removeMember.mutate({ siteId: site.id, userId: m.userId })}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  {resetFor === m.userId && (
                    <ResetLinkPanel
                      create={(resetTotp) => createMemberResetLink(site.id, m.userId, resetTotp)}
                    />
                  )}
                </li>
              ))}
            </ul>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                commands.invite.mutate(
                  { siteId: site.id, email, role },
                  { onSuccess: (res) => (setLink(res.link), setEmailedTo(res.emailedTo), setEmail('')) },
                );
              }}
            >
              <label className="min-w-56 flex-1">
                <span className="text-sm text-muted">{t('auth.email')}</span>
                <input
                  type="email"
                  className={inputClass}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label>
                <span className="text-sm text-muted">{t('sites.roleLabel')}</span>
                <select
                  className={inputClass}
                  value={role}
                  onChange={(e) => setRole(e.target.value as MembershipRole)}
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>
                      {t(`sites.role.${r}`)}
                    </option>
                  ))}
                </select>
              </label>
              <Button type="submit" disabled={!email.includes('@') || commands.invite.isPending}>
                <UserPlus size={18} /> {t('sites.invite')}
              </Button>
            </form>
            {commands.invite.isError && <CommandError error={commands.invite.error} />}
            {link && (
              <Banner
                tone="accent"
                title={emailedTo ? t('mail.sentTo', { email: emailedTo }) : t('sites.inviteCreated')}
              >
                <span className="break-all">{link}</span>
                <button
                  type="button"
                  className="ml-2 inline-flex min-h-11 items-center gap-1 text-accent-text"
                  onClick={() => void navigator.clipboard?.writeText(link)}
                >
                  <Copy size={14} /> {t('sites.copy')}
                </button>
              </Banner>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
